import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOAuthConfig } from '../hooks/useOAuthConfig';

const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MicrosoftLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const EXPLORER_PRICING = {
  plan_id: 'plan_free_explorer',
  plan_type: 'free_explorer',
  plan_label: 'Product Explorer',
  trial_days: 0,
  price_cents: 0,
};

const ExplorerSignupPage = () => {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const { getOAuthUrl, isCustomOAuth } = useOAuthConfig();
  const [authMethod, setAuthMethod] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogleSignup = () => {
    setLoading(true);
    sessionStorage.setItem('is_new_signup', 'true');
    sessionStorage.setItem('signup_pricing', JSON.stringify(EXPLORER_PRICING));
    sessionStorage.setItem('explorer_oauth_signup', 'true');

    const callbackPath = isCustomOAuth() ? '/auth/google/callback' : '/auth/callback';
    const redirectUrl = window.location.origin + callbackPath;

    if (isCustomOAuth()) {
      window.location.href = getOAuthUrl(redirectUrl, EXPLORER_PRICING);
    } else {
      window.location.href = getOAuthUrl(redirectUrl);
    }
  };

  const handleMicrosoftSignup = () => {
    setLoading(true);
    sessionStorage.setItem('is_new_signup', 'true');
    sessionStorage.setItem('signup_pricing', JSON.stringify(EXPLORER_PRICING));
    sessionStorage.setItem('explorer_oauth_signup', 'true');

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams({
      signup_pricing: JSON.stringify(EXPLORER_PRICING),
    });
    window.location.href = `${backendUrl}/api/auth/microsoft/login?${params.toString()}`;
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/auth/register-explorer', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password: password.trim(),
      });
      await refreshAuth();
      toast.success("Welcome aboard! Let's build your product.");
      navigate('/products/new');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400 font-medium">Product Explorer</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Start building your product
          </h1>
          <p className="text-sm text-gray-500">
            Free. No credit card. Just you and your idea.
          </p>
        </div>

        <Card className="bg-[#0a0a0a] border-white/10">
          <CardContent className="pt-6">
            {/* Auth Method Selection */}
            {!authMethod && (
              <div className="space-y-3">
                {/* Google */}
                <button
                  onClick={handleGoogleSignup}
                  disabled={loading}
                  data-testid="explorer-google-signup-btn"
                  className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.04] flex items-center gap-4 transition-all hover:bg-white/[0.08] hover:border-amber-500/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <GoogleLogo />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-white">Continue with Google</p>
                    <p className="text-xs text-gray-500">Fastest way to get started</p>
                  </div>
                </button>

                {/* Microsoft */}
                <button
                  onClick={handleMicrosoftSignup}
                  disabled={loading}
                  data-testid="explorer-microsoft-signup-btn"
                  className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.04] flex items-center gap-4 transition-all hover:bg-white/[0.08] hover:border-amber-500/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <MicrosoftLogo />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-white">Continue with Microsoft</p>
                    <p className="text-xs text-gray-500">Use your work account</p>
                  </div>
                </button>

                {/* Email */}
                <button
                  onClick={() => setAuthMethod('email')}
                  disabled={loading}
                  data-testid="explorer-email-signup-btn"
                  className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.04] flex items-center gap-4 transition-all hover:bg-white/[0.08] hover:border-amber-500/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-gray-300" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-white">Continue with Email</p>
                    <p className="text-xs text-gray-500">Use your email and password</p>
                  </div>
                </button>
              </div>
            )}

            {/* Email Form */}
            {authMethod === 'email' && (
              <form onSubmit={handleEmailSignup} className="space-y-4">
                <button
                  type="button"
                  onClick={() => setAuthMethod(null)}
                  className="flex items-center gap-1 text-sm mb-2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Other sign up options
                </button>

                <div>
                  <Label htmlFor="name" className="text-gray-300 text-sm">Your Name</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Doe"
                      className="pl-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600"
                      data-testid="explorer-name-input"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600"
                      data-testid="explorer-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-gray-300 text-sm">Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-10 pr-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600"
                      data-testid="explorer-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-gray-200 font-medium"
                  data-testid="explorer-signup-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {loading ? 'Creating your workspace...' : 'Start Exploring'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-white hover:underline">Sign in</Link>
        </p>
        <p className="text-center text-xs text-gray-600 mt-2">
          Need the full product?{' '}
          <Link to="/signup" className="text-gray-400 hover:text-white hover:underline">See plans</Link>
        </p>
      </div>
    </div>
  );
};

export default ExplorerSignupPage;
